import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { useEvents } from '../../contexts/EventContext';

/**
 * Guided Event Setup Wizard.
 *
 * Walks an organizer through the standard questions for standing up a new
 * event, then calls POST /events/wizard-create which configures everything
 * (modules from the chosen type, tiers if ticketed, affiliation label, venue,
 * guest-list method, meals, attendee website). On finish it selects the new
 * event and routes to the suggested next step (RSVP import / order import /
 * manual guests).
 *
 * A thin orchestration UI over existing backend building blocks.
 */

interface EventType {
  key: string;
  label: string;
  icon: string;
  description: string;
  modules: string[];
  ticketed: boolean;
}

interface Venue {
  id: string;
  name: string;
}

interface WizardResult {
  event: { id: string; name: string };
  ticketed: boolean;
  guestListMethod: string | null;
  nextStep: 'order-import' | 'guests-manual' | 'rsvp-import';
  onboarding?: { completed: number; total: number; percent: number };
}

type GuestListMethod = 'Rsvp' | 'OrderFile' | 'Manual';

const SEATING_OPTIONS = [
  { key: 'affiliation', label: 'Affiliation / Chapter grouping', hint: 'Seat people from the same chapter/company together' },
  { key: 'tiers', label: 'Ticket tiers', hint: 'VIP / Patron / General seating priority' },
  { key: 'chapters', label: 'Chapters', hint: 'Chapter-aware tables and zones' },
];

const STEPS = ['Basics', 'Type', 'Guests', 'Venue', 'Seating', 'Extras', 'Review'] as const;

export function EventSetupWizard({ onClose }: { onClose: () => void }) {
  const { refresh, selectEvent } = useEvents();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Answers
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [typeKey, setTypeKey] = useState('');
  const [expectedGuests, setExpectedGuests] = useState('');
  const [venueMode, setVenueMode] = useState<'existing' | 'new' | 'tbd'>('tbd');
  const [venueId, setVenueId] = useState('');
  const [newVenueName, setNewVenueName] = useState('');
  const [guestListMethod, setGuestListMethod] = useState<GuestListMethod | ''>('');
  const [seating, setSeating] = useState<string[]>([]);
  const [affiliationLabel, setAffiliationLabel] = useState('');
  const [mealChoices, setMealChoices] = useState(false);
  const [attendeeWebsite, setAttendeeWebsite] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cat = await apiClient<{ eventTypes: EventType[] }>('/events/module-catalog');
        if (!cancelled) setEventTypes(cat.eventTypes || []);
      } catch {
        if (!cancelled) setEventTypes([]);
      }
      try {
        const v = await apiClient<Venue[]>('/venues');
        if (!cancelled) setVenues(Array.isArray(v) ? v.filter((x) => x.name !== 'TBD') : []);
      } catch {
        if (!cancelled) setVenues([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedType = useMemo(
    () => eventTypes.find((t) => t.key === typeKey),
    [eventTypes, typeKey],
  );
  const ticketed = !!selectedType?.ticketed;

  // When the type changes, default the guest-list method sensibly.
  useEffect(() => {
    if (!selectedType) return;
    setGuestListMethod((prev) => prev || (ticketed ? 'OrderFile' : 'Rsvp'));
  }, [selectedType, ticketed]);

  function toggleSeating(key: string) {
    setSeating((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  function canAdvance(): boolean {
    switch (step) {
      case 0: return name.trim().length > 0;
      case 1: return !!typeKey;
      default: return true;
    }
  }

  function next() {
    setError('');
    if (!canAdvance()) {
      setError(step === 0 ? 'Enter an event name.' : 'Choose an event type.');
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }
  function back() { setError(''); setStep((s) => Math.max(s - 1, 0)); }

  async function finish() {
    setSubmitting(true);
    setError('');
    try {
      const result = await apiClient<WizardResult>('/events/wizard-create', {
        method: 'POST',
        body: {
          name: name.trim(),
          eventType: typeKey,
          date: date ? new Date(date).toISOString() : undefined,
          expectedGuests: expectedGuests ? parseInt(expectedGuests, 10) : undefined,
          venueId: venueMode === 'existing' && venueId ? venueId : undefined,
          newVenueName: venueMode === 'new' && newVenueName.trim() ? newVenueName.trim() : undefined,
          guestListMethod: guestListMethod || undefined,
          affiliationLabel: affiliationLabel.trim() || undefined,
          seatingConsiderations: seating,
          mealChoices,
          attendeeWebsite,
        },
      });

      await refresh();
      selectEvent(result.event.id);

      // Route to the suggested next step for this event.
      if (result.nextStep === 'order-import') {
        navigate('/manage-event?tab=ticket-inventory');
      } else if (result.nextStep === 'guests-manual') {
        navigate('/manage-event?tab=guests');
      } else {
        navigate('/manage-event?tab=guests&sub=import');
      }
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not create the event.');
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Create an event</h2>
          <button style={styles.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Stepper */}
        <div style={styles.stepper}>
          {STEPS.map((label, i) => (
            <div key={label} style={styles.stepPill}>
              <span style={{ ...styles.stepDot, ...(i === step ? styles.stepDotActive : i < step ? styles.stepDotDone : {}) }}>
                {i < step ? '✓' : i + 1}
              </span>
              <span style={{ ...styles.stepLabel, ...(i === step ? styles.stepLabelActive : {}) }}>{label}</span>
            </div>
          ))}
        </div>

        <div style={styles.body}>
          {error && <div style={styles.error} onClick={() => setError('')}>{error} <span style={{ float: 'right', fontWeight: 700 }}>×</span></div>}

          {/* Step 0 — Basics */}
          {step === 0 && (
            <div style={styles.stepWrap}>
              <label style={styles.label}>Event name</label>
              <input style={styles.input} autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 2026 Annual Gala" />
              <label style={styles.label}>Date <span style={styles.optional}>(optional — set later)</span></label>
              <input style={styles.input} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          )}

          {/* Step 1 — Type */}
          {step === 1 && (
            <div style={styles.stepWrap}>
              <p style={styles.help}>Pick the kind of event. This pre-selects the right features and tells us whether to set up ticketing or a free RSVP list.</p>
              <div style={styles.typeGrid}>
                {eventTypes.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTypeKey(t.key)}
                    style={{ ...styles.typeCard, ...(typeKey === t.key ? styles.typeCardActive : {}) }}
                  >
                    <div style={styles.typeIcon}>{t.icon}</div>
                    <div style={styles.typeLabel}>{t.label}</div>
                    <div style={styles.typeDesc}>{t.description}</div>
                    <span style={{ ...styles.badge, ...(t.ticketed ? styles.badgeTicketed : styles.badgeFree) }}>
                      {t.ticketed ? 'Ticketed' : 'Non-ticketed'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2 — Guests */}
          {step === 2 && (
            <div style={styles.stepWrap}>
              <label style={styles.label}>How many guests do you expect?</label>
              <input style={styles.input} type="number" min={1} value={expectedGuests} onChange={(e) => setExpectedGuests(e.target.value)} placeholder="e.g. 200" />

              <label style={styles.label}>How will you provide the guest list?</label>
              <div style={styles.choiceCol}>
                {([
                  ['Rsvp', '📝 RSVP list', 'Upload a simple guest/RSVP spreadsheet (no tickets)'],
                  ['OrderFile', '🧾 Order file', 'Import a ticket order file (ticketed events)'],
                  ['Manual', '✍️ Enter manually', 'Add guests one at a time in the app'],
                ] as [GuestListMethod, string, string][]).map(([val, label, hint]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setGuestListMethod(val)}
                    style={{ ...styles.choice, ...(guestListMethod === val ? styles.choiceActive : {}) }}
                  >
                    <span style={styles.choiceLabel}>{label}</span>
                    <span style={styles.choiceHint}>{hint}</span>
                  </button>
                ))}
              </div>
              {ticketed && guestListMethod === 'Rsvp' && (
                <p style={styles.note}>This is a ticketed type — an order file is usually the better fit, but RSVP works too.</p>
              )}
            </div>
          )}

          {/* Step 3 — Venue */}
          {step === 3 && (
            <div style={styles.stepWrap}>
              <label style={styles.label}>Venue</label>
              <div style={styles.choiceCol}>
                <button type="button" onClick={() => setVenueMode('tbd')} style={{ ...styles.choice, ...(venueMode === 'tbd' ? styles.choiceActive : {}) }}>
                  <span style={styles.choiceLabel}>Decide later</span>
                  <span style={styles.choiceHint}>Use a “TBD” placeholder; set the venue when you know it</span>
                </button>
                {venues.length > 0 && (
                  <button type="button" onClick={() => setVenueMode('existing')} style={{ ...styles.choice, ...(venueMode === 'existing' ? styles.choiceActive : {}) }}>
                    <span style={styles.choiceLabel}>Use an existing venue</span>
                    <span style={styles.choiceHint}>Pick from venues you've already created</span>
                  </button>
                )}
                <button type="button" onClick={() => setVenueMode('new')} style={{ ...styles.choice, ...(venueMode === 'new' ? styles.choiceActive : {}) }}>
                  <span style={styles.choiceLabel}>Create a new venue</span>
                  <span style={styles.choiceHint}>Just the name for now; add details later</span>
                </button>
              </div>
              {venueMode === 'existing' && (
                <select style={styles.input} value={venueId} onChange={(e) => setVenueId(e.target.value)}>
                  <option value="">Select a venue…</option>
                  {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              )}
              {venueMode === 'new' && (
                <input style={styles.input} value={newVenueName} onChange={(e) => setNewVenueName(e.target.value)} placeholder="Venue name" />
              )}
            </div>
          )}

          {/* Step 4 — Seating */}
          {step === 4 && (
            <div style={styles.stepWrap}>
              <label style={styles.label}>Seating considerations <span style={styles.optional}>(choose all that apply)</span></label>
              <div style={styles.choiceCol}>
                {SEATING_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => toggleSeating(o.key)}
                    style={{ ...styles.choice, ...(seating.includes(o.key) ? styles.choiceActive : {}) }}
                  >
                    <span style={styles.choiceLabel}>{seating.includes(o.key) ? '☑' : '☐'} {o.label}</span>
                    <span style={styles.choiceHint}>{o.hint}</span>
                  </button>
                ))}
              </div>
              <label style={styles.label}>What do you call the grouping tag? <span style={styles.optional}>(optional)</span></label>
              <input style={styles.input} value={affiliationLabel} onChange={(e) => setAffiliationLabel(e.target.value)} placeholder="e.g. Chapter, Company, Family" />
            </div>
          )}

          {/* Step 5 — Extras */}
          {step === 5 && (
            <div style={styles.stepWrap}>
              <button type="button" onClick={() => setMealChoices((v) => !v)} style={{ ...styles.toggle, ...(mealChoices ? styles.toggleOn : {}) }}>
                <span style={styles.toggleBox}>{mealChoices ? '☑' : '☐'}</span>
                <span style={styles.toggleText}>
                  <strong>Per-guest meal choices</strong>
                  <span style={styles.choiceHint}>Guests pick a meal option (you'll define the menu)</span>
                </span>
              </button>
              <button type="button" onClick={() => setAttendeeWebsite((v) => !v)} style={{ ...styles.toggle, ...(attendeeWebsite ? styles.toggleOn : {}) }}>
                <span style={styles.toggleBox}>{attendeeWebsite ? '☑' : '☐'}</span>
                <span style={styles.toggleText}>
                  <strong>Attendee website / experience</strong>
                  <span style={styles.choiceHint}>A hub where guests see the schedule, their ticket, and info</span>
                </span>
              </button>
            </div>
          )}

          {/* Step 6 — Review */}
          {step === 6 && (
            <div style={styles.stepWrap}>
              <div style={styles.reviewGrid}>
                <ReviewRow label="Name" value={name || '—'} />
                <ReviewRow label="Date" value={date || 'Set later'} />
                <ReviewRow label="Type" value={selectedType ? `${selectedType.icon} ${selectedType.label}` : '—'} />
                <ReviewRow label="Ticketing" value={ticketed ? 'Ticketed (tiers will be created)' : 'Non-ticketed (free RSVP)'} />
                <ReviewRow label="Expected guests" value={expectedGuests || '—'} />
                <ReviewRow label="Venue" value={venueMode === 'existing' ? (venues.find((v) => v.id === venueId)?.name || 'Selected') : venueMode === 'new' ? (newVenueName || 'New venue') : 'TBD (set later)'} />
                <ReviewRow label="Guest list via" value={guestListMethod === 'OrderFile' ? 'Order file' : guestListMethod === 'Manual' ? 'Manual entry' : 'RSVP list'} />
                <ReviewRow label="Seating" value={seating.length ? seating.join(', ') : 'None specified'} />
                <ReviewRow label="Grouping label" value={affiliationLabel || 'Affiliation'} />
                <ReviewRow label="Meal choices" value={mealChoices ? 'Yes' : 'No'} />
                <ReviewRow label="Attendee website" value={attendeeWebsite ? 'Yes' : 'No'} />
              </div>
              <p style={styles.note}>We'll create the event, turn on the right features, {ticketed ? 'set up ticket tiers' : 'prepare the RSVP list'}, and take you to the next step.</p>
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button style={styles.secondaryBtn} onClick={step === 0 ? onClose : back} disabled={submitting}>
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          {step < STEPS.length - 1 ? (
            <button style={styles.primaryBtn} onClick={next} disabled={submitting}>Next</button>
          ) : (
            <button style={styles.primaryBtn} onClick={finish} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create event'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.reviewRow}>
      <span style={styles.reviewLabel}>{label}</span>
      <span style={styles.reviewValue}>{value}</span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modal: { background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '640px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.4rem', borderBottom: '1px solid #e0e0e0' },
  title: { margin: 0, fontSize: '1.2rem', color: '#0f2d1f' },
  close: { background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#6b7280', lineHeight: 1 },
  stepper: { display: 'flex', gap: '0.3rem', padding: '0.8rem 1.4rem', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap' },
  stepPill: { display: 'flex', alignItems: 'center', gap: '5px' },
  stepDot: { width: '20px', height: '20px', borderRadius: '50%', background: '#e5e7eb', color: '#6b7280', fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  stepDotActive: { background: '#1a6b3c', color: '#fff' },
  stepDotDone: { background: '#c9a84c', color: '#fff' },
  stepLabel: { fontSize: '0.72rem', color: '#9ca3af', marginRight: '0.35rem' },
  stepLabelActive: { color: '#0f2d1f', fontWeight: 700 },
  body: { padding: '1.3rem 1.4rem', overflowY: 'auto', flex: 1 },
  stepWrap: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  help: { fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.4rem 0', lineHeight: 1.4 },
  label: { fontSize: '0.8rem', fontWeight: 600, color: '#0f2d1f', marginTop: '0.5rem' },
  optional: { fontWeight: 400, color: '#9ca3af', fontSize: '0.75rem' },
  input: { padding: '0.6rem 0.75rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box', width: '100%' },
  typeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '0.6rem' },
  typeCard: { textAlign: 'left', border: '2px solid #e5e7eb', borderRadius: '10px', padding: '0.8rem', background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '0.2rem', position: 'relative' },
  typeCardActive: { borderColor: '#1a6b3c', background: '#f0f9f4' },
  typeIcon: { fontSize: '1.5rem' },
  typeLabel: { fontWeight: 700, color: '#0f2d1f', fontSize: '0.9rem' },
  typeDesc: { fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.3 },
  badge: { marginTop: '0.4rem', alignSelf: 'flex-start', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '9999px' },
  badgeTicketed: { background: '#e3f2fd', color: '#1565c0' },
  badgeFree: { background: '#dcfce7', color: '#166534' },
  choiceCol: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  choice: { textAlign: 'left', border: '2px solid #e5e7eb', borderRadius: '8px', padding: '0.7rem 0.85rem', background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '0.15rem' },
  choiceActive: { borderColor: '#1a6b3c', background: '#f0f9f4' },
  choiceLabel: { fontWeight: 600, color: '#0f2d1f', fontSize: '0.88rem' },
  choiceHint: { fontSize: '0.75rem', color: '#6b7280' },
  toggle: { textAlign: 'left', border: '2px solid #e5e7eb', borderRadius: '8px', padding: '0.8rem 0.9rem', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' },
  toggleOn: { borderColor: '#1a6b3c', background: '#f0f9f4' },
  toggleBox: { fontSize: '1.1rem', color: '#1a6b3c', lineHeight: 1 },
  toggleText: { display: 'flex', flexDirection: 'column', gap: '0.15rem', color: '#0f2d1f', fontSize: '0.9rem' },
  note: { fontSize: '0.78rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '0.5rem 0.7rem', margin: '0.6rem 0 0 0', lineHeight: 1.4 },
  reviewGrid: { display: 'flex', flexDirection: 'column', gap: '0.3rem', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.6rem 0.8rem' },
  reviewRow: { display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.25rem 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.85rem' },
  reviewLabel: { color: '#6b7280' },
  reviewValue: { color: '#0f2d1f', fontWeight: 600, textAlign: 'right' },
  footer: { display: 'flex', justifyContent: 'space-between', gap: '0.6rem', padding: '1rem 1.4rem', borderTop: '1px solid #e0e0e0' },
  primaryBtn: { background: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 1.4rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' },
  secondaryBtn: { background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '6px', padding: '0.6rem 1.4rem', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' },
  error: { color: '#d32f2f', background: '#fdecea', padding: '0.6rem 0.9rem', borderRadius: '6px', marginBottom: '0.9rem', fontSize: '0.85rem', cursor: 'pointer' },
};
